import BaseController from "./BaseController";
import JSONModel from "sap/ui/model/json/JSONModel";
import AuthService from "../service/AuthService";
import GroupsService from "../service/GroupsService";
import type {
	GroupRecord,
	GroupDetail,
	GroupSummary,
	CreateGroupPayload,
	UpdateGroupPayload,
} from "../service/GroupsService";
import MessageBox from "sap/m/MessageBox";
import Event from "sap/ui/base/Event";
import Control from "sap/ui/core/Control";
import Dialog from "sap/m/Dialog";
import MultiInput from "sap/m/MultiInput";
import Token from "sap/m/Token";
import ValueHelp from "../util/ValueHelp";

interface LookupItem {
	key: string;
	text: string;
}

interface UserLookupItem {
	id: number;
	email: string;
	name: string;
}

interface GroupsPageModel {
	items: GroupRecord[];
	busy: boolean;
	lookups: {
		roles: LookupItem[];
		permissions: LookupItem[];
		users: UserLookupItem[];
		groups: GroupSummary[];
	};
	canView: boolean;
	dialogTitle: string;
	dialogMode: "create" | "edit";
	isDialogOpen: boolean;
	isEditMode: boolean;
	actionsEnabled: boolean;
}

interface GroupsEditModel {
	id: number | null;
	name: string;
	description: string;
	roles: string[];
	permissions: string[];
	memberIds: number[];
	variables: Array<{ key: string; value: string }>;
	includedGroupIds: number[];
	/** Derived server-side and shown read-only, so the admin sees what nesting adds. */
	effectiveRoles: string[];
	effectivePermissions: string[];
}

function createLookupItems(entries: string[]): LookupItem[] {
	return entries.map((entry) => ({ key: entry, text: entry }));
}

function createEmptyEditModel(): GroupsEditModel {
	return {
		id: null,
		name: "",
		description: "",
		roles: [],
		permissions: [],
		memberIds: [],
		variables: [],
		includedGroupIds: [],
		effectiveRoles: [],
		effectivePermissions: [],
	};
}

type GroupsPageJsonModel = JSONModel & {
	getData(): GroupsPageModel;
	setData(data: GroupsPageModel): void;
	getProperty(path: string): unknown;
	setProperty(path: string, value: unknown): void;
};

type GroupsEditJsonModel = JSONModel & {
	getData(): GroupsEditModel;
	setData(data: GroupsEditModel): void;
	getProperty(path: string): unknown;
	setProperty(path: string, value: unknown): void;
};

/**
 * @namespace com.ang.portal.controller
 */
export default class Groups extends BaseController {
	private dialogOpenLock = false;

	public onInit(): void {
		this.setModel(
			new JSONModel({
				items: [],
				busy: false,
				lookups: {
					roles: [],
					permissions: [],
					users: [],
					groups: [],
				},
				canView: false,
				dialogTitle: "",
				dialogMode: "create",
				isDialogOpen: false,
				isEditMode: false,
				actionsEnabled: true,
			}),
			"groupsPage",
		);
		this.setModel(new JSONModel(createEmptyEditModel()), "groupsEdit");

		// The view is instantiated eagerly with the Home shell, for every user.
		// Nothing is fetched here — a user without group access would otherwise
		// fire /api/groups and /api/groups/lookups at login and get a 403 toast.
		// Home invokes refreshPage() when the section is actually navigated to.
		this.refreshPermissions();
	}

	public refreshPage(): void {
		this.refreshPermissions();
		if (AuthService.isAuthenticated() && this.canViewGroups()) {
			void this.loadGroupsPage();
		}
	}

	/**
	 * Group administration is role-gated, mirroring the `canViewGroups` flag the
	 * Home shell uses to show or hide the side-nav entry and this view.
	 */
	private refreshPermissions(): void {
		const roles = AuthService.getUserInfo()?.roles ?? [];
		this.getPageModel().setProperty(
			"/canView",
			roles.includes("system:admin") || roles.includes("users:admin"),
		);
	}

	private canViewGroups(): boolean {
		return this.getPageModel().getProperty("/canView") === true;
	}

	private getPageModel(): GroupsPageJsonModel {
		return this.getModel("groupsPage") as GroupsPageJsonModel;
	}

	private getEditModel(): GroupsEditJsonModel {
		return this.getModel("groupsEdit") as GroupsEditJsonModel;
	}

	private getGroupFromEvent(oEvent: Event): GroupRecord | null {
		const source = oEvent.getSource();
		if (!(source instanceof Control)) {
			return null;
		}

		const rawGroup = source
			.getBindingContext("groupsPage")
			?.getObject() as unknown;
		if (!rawGroup || typeof rawGroup !== "object") {
			return null;
		}

		return rawGroup as GroupRecord;
	}

	private async loadGroupsPage(): Promise<void> {
		const pageModel = this.getPageModel();
		pageModel.setProperty("/busy", true);

		try {
			const [items, lookups] = await Promise.all([
				GroupsService.getGroups(),
				GroupsService.getLookups(),
			]);

			pageModel.setProperty("/items", items);
			pageModel.setProperty("/lookups", {
				roles: createLookupItems(lookups.roles),
				permissions: createLookupItems(lookups.permissions),
				users: lookups.users,
				groups: lookups.groups ?? [],
			});
		} catch (error) {
			await this.showToastForError(error, "groupsLoadError");
		} finally {
			pageModel.setProperty("/busy", false);
		}
	}

	private async openDialog(mode: "create" | "edit", group?: GroupRecord): Promise<void> {
		const pageModel = this.getPageModel();
		const dialog = this.byId("groupDialog") as Dialog;
		const dialogOpenState = pageModel.getProperty("/isDialogOpen") === true;
		const isDialogOpen =
			typeof (dialog as unknown as { isOpen?: () => boolean }).isOpen ===
			"function"
				? (dialog as unknown as { isOpen: () => boolean }).isOpen()
				: false;

		if (this.dialogOpenLock || dialogOpenState || isDialogOpen) {
			return;
		}
		this.dialogOpenLock = true;

		const editModel = this.getEditModel();

		if (mode === "create") {
			editModel.setData(createEmptyEditModel());
			pageModel.setProperty("/isEditMode", false);
		} else if (group) {
			pageModel.setProperty("/isEditMode", true);
			// Load full detail to get members
			try {
				const detail = await GroupsService.getGroup(group.id);
				editModel.setData({
					id: detail.id,
					name: detail.name,
					description: detail.description,
					roles: [...detail.roles],
					permissions: [...detail.permissions],
					memberIds: detail.members.map((m) => m.id),
					variables: detail.variables.map((v) => ({ ...v })),
					includedGroupIds: detail.includedGroups.map((g) => g.id),
					effectiveRoles: detail.effectiveRoles ?? [],
					effectivePermissions: detail.effectivePermissions ?? [],
				});

				// Sync member tokens with email display
				this.syncMemberTokens(detail.members);
				this.syncIncludedGroupTokens(detail.includedGroups);
			} catch (error) {
				await this.showToastForError(error, "groupsLoadError");
				this.dialogOpenLock = false;
				return;
			}
		}

		pageModel.setProperty("/dialogMode", mode);
		pageModel.setProperty("/isDialogOpen", true);
		pageModel.setProperty("/actionsEnabled", false);
		void this.getResourceBundle().then((bundle) => {
			pageModel.setProperty(
				"/dialogTitle",
				bundle.getText(
					mode === "create" ? "groupsDialogCreateTitle" : "groupsDialogEditTitle",
				),
			);
		});

		const editData = editModel.getData() as GroupsEditModel;
		this.syncTokensToMultiInput("groupRolesInput", editData.roles);
		this.syncTokensToMultiInput("groupPermissionsInput", editData.permissions);

		if (mode === "create") {
			this.syncTokensToMultiInput("groupMembersInput", []);
			this.syncIncludedGroupTokens([]);
		}

		dialog.open();
	}

	private syncMemberTokens(members: Array<{ id: number; email: string; name: string }>): void {
		const oInput = this.byId("groupMembersInput") as MultiInput;
		oInput.removeAllTokens();
		members.forEach((m) => {
			oInput.addToken(new Token({ key: String(m.id), text: m.name || m.email }));
		});
	}

	private syncIncludedGroupTokens(groups: GroupSummary[]): void {
		const oInput = this.byId("groupIncludedGroupsInput") as MultiInput;
		oInput.removeAllTokens();
		groups.forEach((g) => {
			oInput.addToken(new Token({ key: String(g.id), text: g.name }));
		});
	}

	public onOpenCreateDialog(): void {
		void this.openDialog("create");
	}

	public onEditGroupPress(oEvent: Event): void {
		const group = this.getGroupFromEvent(oEvent);
		if (group) {
			void this.openDialog("edit", group);
		}
	}

	public onDialogAfterClose(): void {
		const pageModel = this.getPageModel();
		pageModel.setProperty("/isDialogOpen", false);
		pageModel.setProperty("/actionsEnabled", true);
		this.dialogOpenLock = false;
	}

	public onDialogAfterOpen(): void {
		this.dialogOpenLock = false;
	}

	public onCloseDialog(): void {
		const pageModel = this.getPageModel();
		pageModel.setProperty("/isDialogOpen", false);
		pageModel.setProperty("/actionsEnabled", true);
		(this.byId("groupDialog") as Dialog).close();
	}

	public async onSaveGroup(): Promise<void> {
		const pageModel = this.getPageModel();
		const editModel = this.getEditModel();
		const dialogMode = pageModel.getProperty("/dialogMode") as GroupsPageModel["dialogMode"];
		const data = editModel.getData() as GroupsEditModel;

		if (!data.name.trim()) {
			await this.showToast("groupsValidationNameRequired");
			return;
		}

		const roles = this.getTokenKeys("groupRolesInput");
		const permissions = this.getTokenKeys("groupPermissionsInput");
		const memberIds = this.getMemberIds("groupMembersInput");
		const includedGroupIds = this.getMemberIds("groupIncludedGroupsInput");
		const variables = (data.variables ?? []).filter((v) => v.key.trim());

		pageModel.setProperty("/busy", true);
		try {
			if (dialogMode === "create") {
				const payload: CreateGroupPayload = {
					name: data.name.trim(),
					description: data.description.trim(),
					roles,
					permissions,
					memberIds,
					variables,
					includedGroupIds,
				};

				await GroupsService.createGroup(payload);
				await this.showToast("groupsCreateSuccess");
			} else if (data.id !== null) {
				const payload: UpdateGroupPayload = {
					name: data.name.trim(),
					description: data.description.trim(),
					roles,
					permissions,
					memberIds,
					variables,
					includedGroupIds,
				};

				await GroupsService.updateGroup(data.id, payload);
				await this.showToast("groupsUpdateSuccess");
			}

			this.onCloseDialog();
			await this.loadGroupsPage();
		} catch (error) {
			await this.showToastForError(
				error,
				dialogMode === "create" ? "groupsCreateError" : "groupsUpdateError",
			);
		} finally {
			pageModel.setProperty("/busy", false);
		}
	}

	public async onDeleteGroupPress(oEvent: Event): Promise<void> {
		const group = this.getGroupFromEvent(oEvent);
		if (!group) {
			return;
		}

		const bundle = await this.getResourceBundle();
		const confirmed = await new Promise<boolean>((resolve) => {
			MessageBox.confirm(bundle.getText("groupsDeleteConfirm", [group.name]), {
				actions: [MessageBox.Action.OK, MessageBox.Action.CANCEL],
				emphasizedAction: MessageBox.Action.OK,
				onClose: (action) => resolve(action === "OK"),
			});
		});

		if (!confirmed) {
			return;
		}

		const pageModel = this.getPageModel();
		pageModel.setProperty("/busy", true);
		try {
			await GroupsService.deleteGroup(group.id);
			await this.showToast("groupsDeleteSuccess");
			await this.loadGroupsPage();
		} catch (error) {
			await this.showToastForError(error, "groupsDeleteError");
		} finally {
			pageModel.setProperty("/busy", false);
		}
	}

	// --- Value Help ---

	private syncTokensToMultiInput(inputId: string, keys: string[]): void {
		const oInput = this.byId(inputId) as MultiInput;
		oInput.removeAllTokens();
		keys.forEach((key) => {
			oInput.addToken(new Token({ key, text: key }));
		});
	}

	private getTokenKeys(inputId: string): string[] {
		const oInput = this.byId(inputId) as MultiInput;
		return oInput.getTokens().map((token) => token.getKey());
	}

	private getMemberIds(inputId: string): number[] {
		const oInput = this.byId(inputId) as MultiInput;
		return oInput.getTokens().map((token) => Number(token.getKey()));
	}

	private openValueHelpDialog(
		inputId: string,
		lookupPath: string,
		titleKey: string,
	): void {
		const oInput = this.byId(inputId) as MultiInput;
		const pageModel = this.getPageModel();
		const lookups = pageModel.getProperty(lookupPath) as LookupItem[];

		void this.getResourceBundle().then((bundle) => {
			ValueHelp.openMultiSelect({
				title: bundle.getText(titleKey),
				entries: lookups,
				selectedKeys: oInput.getTokens().map((token) => token.getKey()),
				onConfirm: (keys) => {
					oInput.removeAllTokens();
					keys.forEach((key) => {
						const match = lookups.find((item) => item.key === key);
						oInput.addToken(
							new Token({ key, text: match ? match.text : key }),
						);
					});
				},
			});
		});
	}

	public onRolesValueHelp(): void {
		this.openValueHelpDialog(
			"groupRolesInput",
			"/lookups/roles",
			"groupsValueHelpRoles",
		);
	}

	public onPermissionsValueHelp(): void {
		this.openValueHelpDialog(
			"groupPermissionsInput",
			"/lookups/permissions",
			"groupsValueHelpPermissions",
		);
	}

	public onIncludedGroupsValueHelp(): void {
		const oInput = this.byId("groupIncludedGroupsInput") as MultiInput;
		const editModel = this.getEditModel();
		const pageModel = this.getPageModel();
		const currentId = (editModel.getData() as GroupsEditModel).id;
		const groups = pageModel.getProperty("/lookups/groups") as GroupSummary[];

		// A group cannot include itself. Deeper loops (A includes B, B includes A)
		// are rejected by the server, which is the only side that can see the
		// existing nesting — the message surfaces through the usual error toast.
		const entries = groups
			.filter((group) => group.id !== currentId)
			.map((group) => ({ key: String(group.id), text: group.name }));

		void this.getResourceBundle().then((bundle) => {
			ValueHelp.openMultiSelect({
				title: bundle.getText("groupsValueHelpIncludedGroups"),
				entries,
				selectedKeys: oInput.getTokens().map((token) => token.getKey()),
				onConfirm: (keys) => {
					oInput.removeAllTokens();
					keys.forEach((key) => {
						const match = entries.find((entry) => entry.key === key);
						if (match) {
							oInput.addToken(new Token({ key, text: match.text }));
						}
					});
				},
			});
		});
	}

	public onMembersValueHelp(): void {
		const oInput = this.byId("groupMembersInput") as MultiInput;
		const pageModel = this.getPageModel();
		const users = pageModel.getProperty("/lookups/users") as UserLookupItem[];
		// Members are identified by id, so the search can match on name or email
		// without the confirm handler having to map a list row back to a user.
		const entries = users.map((user) => ({
			key: String(user.id),
			text: user.name || user.email,
			description: user.email,
		}));

		void this.getResourceBundle().then((bundle) => {
			ValueHelp.openMultiSelect({
				title: bundle.getText("groupsValueHelpMembers"),
				entries,
				selectedKeys: oInput.getTokens().map((token) => token.getKey()),
				onConfirm: (keys) => {
					oInput.removeAllTokens();
					keys.forEach((key) => {
						const match = entries.find((entry) => entry.key === key);
						if (match) {
							oInput.addToken(new Token({ key, text: match.text }));
						}
					});
				},
			});
		});
	}

	public onAddVariable(): void {
		const editModel = this.getEditModel();
		const variables = (editModel.getProperty("/variables") as Array<{ key: string; value: string }>) ?? [];
		variables.push({ key: "", value: "" });
		editModel.setProperty("/variables", variables);
	}

	public onDeleteVariable(oEvent: Event): void {
		const source = oEvent.getSource();
		if (!(source instanceof Control)) return;
		const ctx = source.getBindingContext("groupsEdit");
		if (!ctx) return;
		const path = ctx.getPath();
		const index = parseInt(path.split("/").pop() ?? "", 10);
		if (isNaN(index)) return;
		const editModel = this.getEditModel();
		const variables = (editModel.getProperty("/variables") as Array<{ key: string; value: string }>) ?? [];
		variables.splice(index, 1);
		editModel.setProperty("/variables", [...variables]);
	}

	public onTokenUpdate(oEvent: Event): void {
		const type = oEvent.getParameter("type" as never) as string;
		if (type === "removed") {
			const removedTokens: Token[] = oEvent.getParameter(
				"removedTokens" as never,
			);
			const oInput: MultiInput = oEvent.getSource();
			removedTokens.forEach((token) => {
				oInput.removeToken(token);
			});
		}
	}
}
